import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './VarianceReport.css';

function buildReportUrl(endpoint, isAdmin, selectedLocationId) {
  if (!isAdmin || selectedLocationId === '') return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}consignment_location_id=${encodeURIComponent(selectedLocationId)}`;
}

function cellDisplay(row, col) {
  if (col.format) return col.format(row[col.key], row);
  const v = row[col.key];
  return v != null && v !== '' ? String(v) : '—';
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {string} props.endpoint - e.g. /api/reports/cycle-count-records
 * @param {string} props.exportFileSlug - filename prefix
 * @param {{ key: string, header: string, format?: (value: unknown, row: object) => string }[]} props.columns
 * @param {typeof props.columns} [props.exportColumns] - optional wider columns for Excel only
 * @param {string} [props.emptyIcon]
 * @param {(data: unknown) => unknown[]} [props.transformResponse] - if API returns non-array
 */
const ReportDataPage = ({
  title,
  subtitle,
  endpoint,
  exportFileSlug,
  columns,
  exportColumns: exportColumnsProp,
  emptyIcon = '📋',
  transformResponse,
}) => {
  const exportColumns = exportColumnsProp || columns;
  const navigate = useNavigate();
  const { fetchWithAuth, user, isAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const url = buildReportUrl(endpoint, isAdmin, selectedLocationId);
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to load report');
      }
      const data = await response.json();
      let list = transformResponse ? transformResponse(data) : data;
      if (!Array.isArray(list)) list = [];
      setRows(list);
    } catch (err) {
      setError(err.message || 'Failed to load report');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, endpoint, isAdmin, selectedLocationId, transformResponse]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleExport = () => {
    const exportRows = rows.map((row) => {
      const o = {};
      for (const col of exportColumns) {
        const raw = row[col.key];
        o[col.header] = col.format ? col.format(raw, row) : raw != null && raw !== '' ? String(raw) : '';
      }
      return o;
    });
    const stamp = new Date().toISOString().slice(0, 10);
    exportRowsToExcel(`${exportFileSlug}-${stamp}`, title.slice(0, 28), exportRows);
  };

  return (
    <Layout>
      <div className="variance-container">
        <div className="variance-header">
          <div className="variance-header-left">
            <button type="button" className="variance-back" onClick={() => navigate('/reports')}>
              ← Back to Reports
            </button>
            <h1>{title}</h1>
          </div>
          <div className="variance-header-actions">
            {isAdmin && (
              <label className="variance-filter">
                <span className="variance-filter-label">Location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="variance-filter-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location #${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isAdmin && user?.consignmentLocationName && (
              <span className="variance-badge">Location: {user.consignmentLocationName}</span>
            )}
            <button type="button" className="variance-refresh" onClick={loadRecords}>
              🔄 Refresh
            </button>
            <button type="button" className="variance-refresh" onClick={handleExport} disabled={rows.length === 0}>
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="variance-subtitle">{subtitle}</p>
        {error && <div className="variance-error">{error}</div>}
        {isLoading ? (
          <div className="variance-loading">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="variance-empty">
            <div className="variance-empty-icon">{emptyIcon}</div>
            <h2>No records</h2>
            <p>Nothing to show for the current filter.</p>
          </div>
        ) : (
          <div className="variance-table-wrap">
            <table className="variance-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id != null ? String(row.id) : JSON.stringify(row)}>
                    {columns.map((col) => (
                      <td key={col.key}>{cellDisplay(row, col)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportDataPage;
